/**
 * Optimized CountryLocator component.
 *
 * Performance improvements:
 * - Web Worker for expensive geo calculations (off main thread)
 * - Cached GeoJSON conversion
 * - Debounced dimension changes
 * - CSS transform for zoom (GPU accelerated)
 * - Skeleton loading state
 * - ZERO React re-renders during pan/zoom (all DOM updates are direct)
 * - Transform stored in ref, zoom indicator updated via DOM
 * - Pre-rendered copy elements with visibility controlled via DOM
 * - SVG <use> elements for path deduplication
 * - pointer-events: none on all paths (eliminates hit testing)
 * - Dynamic path simplification via topojson-simplify (reduces path complexity)
 * - CSS animations instead of Framer Motion (lower overhead)
 * - Dynamic resolution rendering based on zoom level (debounced)
 */

import { useEffect, useLayoutEffect, useState, useRef, memo, useCallback, useMemo } from 'react';
import { useReducedMotion } from 'framer-motion';
import { select } from 'd3-selection';
import { zoom as d3Zoom, zoomIdentity } from 'd3-zoom';
import { loadWorldData } from '@/lib/map-data';
import { getCountryByCode, getAtlasName } from '@/data/countries';
import type { Topology } from 'topojson-specification';

interface CountryLocatorProps {
  countryName: string;
  countryCode: string;
}

interface GeoResult {
  allCountriesPath: string;
  targetCountryPath: string;
  projectionConfig: {
    scale: number;
    translate: [number, number];
    center?: [number, number];
  } | null;
  isTinyCountry: boolean;
  markerPosition: { x: number; y: number } | null;
  initialZoom: number;
  countryScreenCenter: { x: number; y: number } | null;
  countryScreenSize: number;
  worldWidth: number;
  hasTarget: boolean;
}

/**
 * Get simplification level based on current zoom
 */
function getSimplificationLevel(zoom: number): 'high' | 'medium' | 'low' {
  if (zoom < 2) return 'high';
  if (zoom < 8) return 'medium';
  return 'low';
}

// Eagerly start loading the worker module
let geoWorkerPromise: Promise<Worker> | null = null;

function getGeoWorker(): Promise<Worker> {
  if (!geoWorkerPromise) {
    geoWorkerPromise = import('../workers/geo-worker.ts?worker').then(
      (module) => new module.default()
    );
  }
  return geoWorkerPromise;
}

// Preload worker immediately on module load
getGeoWorker();

// Request counter for matching responses
let requestId = 0;

// Cache for geo results to avoid recalculation
const geoResultCache = new Map<string, GeoResult>();

function getCacheKey(countryCode: string, width: number, height: number): string {
  // Round dimensions to avoid cache misses from tiny floating point differences
  return `${countryCode}-${Math.round(width)}-${Math.round(height)}`;
}

/**
 * Calculate geo data using Web Worker.
 */
async function calculateInWorker(
  worldData: Topology,
  backgroundWorldData: Topology,
  countryName: string,
  atlasName: string,
  mapHints: unknown,
  dimensions: { width: number; height: number },
  simplificationLevel?: 'high' | 'medium' | 'low'
): Promise<GeoResult> {
  const worker = await getGeoWorker();
  const id = `req-${++requestId}`;

  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => {
      reject(new Error('Worker timeout'));
    }, 10000);

    const handler = (event: MessageEvent) => {
      if (event.data.id === id) {
        clearTimeout(timeout);
        worker.removeEventListener('message', handler);
        resolve(event.data.result);
      }
    };

    worker.addEventListener('message', handler);
    worker.postMessage({
      type: 'calculate',
      id,
      worldData,
      backgroundWorldData,
      countryName,
      atlasName,
      mapHints,
      dimensions,
      simplificationLevel,
    });
  });
}

/**
 * Skeleton placeholder while loading.
 */
const MapSkeleton = memo(function MapSkeleton() {
  return (
    <div className="w-full h-full bg-blue-50/30 flex items-center justify-center">
      <div className="animate-pulse flex flex-col items-center gap-2">
        <div className="w-32 h-24 bg-slate-200/50 rounded" />
        <div className="w-20 h-3 bg-slate-200/50 rounded" />
      </div>
    </div>
  );
});

/**
 * Optimized CountryLocator component.
 */
export const CountryLocatorOptimized = memo(function CountryLocatorOptimized({
  countryName,
  countryCode,
}: CountryLocatorProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const gRef = useRef<SVGGElement>(null);
  const zoomRef = useRef<ReturnType<typeof d3Zoom<SVGSVGElement, unknown>> | null>(null);

  // Store transform in ref to avoid re-renders during pan/zoom
  const transformRef = useRef({ x: 0, y: 0, k: 1 });

  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [worldData10m, setWorldData10m] = useState<Topology | null>(null);
  const [worldData50m, setWorldData50m] = useState<Topology | null>(null);
  const [geoResult, setGeoResult] = useState<GeoResult | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasAppliedInitialZoom, setHasAppliedInitialZoom] = useState(false);
  const [currentSimplificationLevel, setCurrentSimplificationLevel] = useState<'high' | 'medium' | 'low'>('high');
  const [currentZoom, setCurrentZoom] = useState(1);
  const simplificationDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const shouldReduceMotion = useReducedMotion();
  const countryData = getCountryByCode(countryCode);
  const atlasName = countryData?.mapHints?.nameInAtlas ?? getAtlasName(countryName);

  // Get initial dimensions synchronously before paint
  useLayoutEffect(() => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) {
      setDimensions({ width: rect.width, height: rect.height });
    }
  }, []);

  // Track dimension changes with debounced resize
  useEffect(() => {
    if (!containerRef.current) return;

    let timeoutId: ReturnType<typeof setTimeout>;

    const observer = new ResizeObserver((entries) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        const entry = entries[0];
        if (entry) {
          const { width, height } = entry.contentRect;
          if (width > 0 && height > 0) {
            setDimensions((prev) => {
              if (Math.round(prev.width) !== Math.round(width) ||
                  Math.round(prev.height) !== Math.round(height)) {
                return { width, height };
              }
              return prev;
            });
          }
        }
      }, 100);
    });

    observer.observe(containerRef.current);
    return () => {
      observer.disconnect();
      clearTimeout(timeoutId);
    };
  }, []);

  // Load map data - 10m for target country (crisp), 50m for background (simpler strokes)
  useEffect(() => {
    let mounted = true;
    // Load both resolutions in parallel
    Promise.all([
      loadWorldData('10m'),
      loadWorldData('50m'),
    ]).then(([data10m, data50m]) => {
      if (mounted) {
        setWorldData10m(data10m);
        setWorldData50m(data50m);
      }
    });
    return () => { mounted = false; };
  }, []);

  // Calculate geo data in worker when inputs change (including simplification level)
  useEffect(() => {
    if (!worldData10m || !worldData50m || dimensions.width === 0 || dimensions.height === 0) {
      return;
    }

    // Include simplification level in cache key
    const cacheKey = `${getCacheKey(countryCode, dimensions.width, dimensions.height)}-${currentSimplificationLevel}`;
    const cached = geoResultCache.get(cacheKey);
    if (cached) {
      setGeoResult(cached);
      setIsLoading(false);
      // Only reset initial zoom for new country, not for simplification changes
      if (!geoResult || geoResult.targetCountryPath !== cached.targetCountryPath) {
        setHasAppliedInitialZoom(false);
      }
      return;
    }

    let cancelled = false;
    // Only show loading for initial load, not simplification changes
    if (!geoResult) {
      setIsLoading(true);
    }

    calculateInWorker(
      worldData10m,      // 10m for target country (crisp detail)
      worldData50m,      // 50m for background (simpler, thinner strokes)
      countryName,
      atlasName,
      countryData?.mapHints,
      dimensions,
      currentSimplificationLevel
    )
      .then((result) => {
        if (!cancelled) {
          // Cache the result
          geoResultCache.set(cacheKey, result);
          // Limit cache size to 100 entries (more entries for different simplification levels)
          if (geoResultCache.size > 100) {
            const firstKey = geoResultCache.keys().next().value;
            if (firstKey) geoResultCache.delete(firstKey);
          }
          setGeoResult(result);
          setIsLoading(false);
          // Only reset initial zoom for new country (when previous result was null or different country)
          if (!geoResult) {
            setHasAppliedInitialZoom(false);
          }
        }
      })
      .catch((err) => {
        console.error('Geo calculation failed:', err);
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [worldData10m, worldData50m, countryName, countryCode, atlasName, countryData?.mapHints, dimensions, currentSimplificationLevel]);

  // Set up zoom behavior with ZERO React state updates
  useEffect(() => {
    if (!svgRef.current || !gRef.current || dimensions.width === 0) return;

    const svg = select(svgRef.current);

    const zoom = d3Zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.5, Infinity])
      // Allow infinite horizontal panning
      .translateExtent([[-Infinity, -Infinity], [Infinity, Infinity]])
      .on('zoom', (event) => {
        const { x, y, k } = event.transform;

        // Update ref immediately (no re-render)
        transformRef.current = { x, y, k };

        // Apply transform to g element - this is the ONLY modification during zoom
        // Using SVG transform attribute (not CSS) for potentially better Safari performance
        if (gRef.current) {
          gRef.current.setAttribute('transform', `translate(${x}, ${y}) scale(${k})`);
        }

        // Debounce simplification level and zoom tracking to avoid lag during zoom
        const newLevel = getSimplificationLevel(k);
        if (simplificationDebounceRef.current) {
          clearTimeout(simplificationDebounceRef.current);
        }
        simplificationDebounceRef.current = setTimeout(() => {
          setCurrentSimplificationLevel((prev) => {
            if (prev !== newLevel) {
              return newLevel;
            }
            return prev;
          });
          // Update current zoom for marker visibility
          setCurrentZoom(k);
        }, 300); // 300ms debounce
      });

    zoomRef.current = zoom;
    svg.call(zoom);
    svg.style('touch-action', 'none');

    return () => {
      svg.on('.zoom', null);
      zoomRef.current = null;
      if (simplificationDebounceRef.current) {
        clearTimeout(simplificationDebounceRef.current);
      }
    };
  }, [dimensions.width, dimensions.height]);

  // Apply initial zoom centered on the country
  useEffect(() => {
    if (!svgRef.current || !zoomRef.current || !geoResult || hasAppliedInitialZoom) return;
    if (!geoResult.hasTarget || geoResult.initialZoom <= 1) return;
    if (!geoResult.countryScreenCenter) return;

    const svg = select(svgRef.current);
    const { initialZoom, countryScreenCenter } = geoResult;

    // Center the zoom on the country's screen-space position
    // Transform formula: translate to viewport center, scale, then translate from country center
    const transform = zoomIdentity
      .translate(dimensions.width / 2, dimensions.height / 2)
      .scale(initialZoom)
      .translate(-countryScreenCenter.x, -countryScreenCenter.y);

    // Apply transform using the zoom behavior
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (svg.call as any)(zoomRef.current.transform, transform);
    setHasAppliedInitialZoom(true);
    // Update currentZoom to match the initial zoom
    setCurrentZoom(initialZoom);
  }, [geoResult, hasAppliedInitialZoom, dimensions]);

  const { allCountriesPath, targetCountryPath, markerPosition, countryScreenSize = 0, worldWidth = 0 } = geoResult || {};
  const showInitialSkeleton = isLoading && !geoResult && dimensions.width === 0;

  // Show marker for visually small countries
  // If initialZoom > 1, the country needed zooming to be visible, so it's "small"
  // The higher the initialZoom, the smaller the country appears at base level
  const showTinyMarker = useMemo(() => {
    if (!markerPosition || !geoResult) return false;

    const initialZoom = geoResult.initialZoom || 1;

    // Show marker if initialZoom > 1.5 (country needed significant zoom to be visible)
    // This is a reliable indicator that the country is hard to see at base level
    if (initialZoom <= 1.5) return false;

    // Hide if user has zoomed in significantly (4x the initial zoom)
    // currentZoom tracks the d3 zoom transform's k value which includes initialZoom
    const hideThreshold = initialZoom * 4;
    return currentZoom < hideThreshold;
  }, [markerPosition, currentZoom, geoResult]);

  // Pre-generate copy indices from -10 to +10 (21 total - enough for any zoom level)
  // These are rendered once; visibility is controlled via direct DOM manipulation in zoom handler
  const copyIndices = Array.from({ length: 21 }, (_, i) => i - 10);

  return (
    <div ref={containerRef} className="w-full h-full bg-blue-50/30 relative">
      {/* Show skeleton only before we have dimensions */}
      {showInitialSkeleton && <MapSkeleton />}

      {dimensions.width > 0 && dimensions.height > 0 && (
        <svg
          ref={svgRef}
          width={dimensions.width}
          height={dimensions.height}
          viewBox={`0 0 ${dimensions.width} ${dimensions.height}`}
          className="block cursor-grab active:cursor-grabbing"
          style={{ overflow: 'hidden' }}
        >
              {/* Define paths once, reuse with <use> elements for better performance */}
              {/* pointer-events: none eliminates expensive hit testing on complex paths */}
              <defs>
                {allCountriesPath && (
                  <path
                    id="world-bg"
                    d={allCountriesPath}
                    fill="white"
                    stroke="#94a3b8"
                    strokeWidth="0.15"
                    style={{ pointerEvents: 'none' }}
                  />
                )}
                {targetCountryPath && (
                  <path
                    id="target-country"
                    d={targetCountryPath}
                    fill="white"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    vectorEffect="non-scaling-stroke"
                    className="text-primary"
                    style={{ pointerEvents: 'none' }}
                  />
                )}
              </defs>

              {/* Removed willChange from g element - doesn't help SVG, wastes memory */}
              <g ref={gRef} style={{ pointerEvents: 'none' }}>
                {/* Container for copy groups - all copies visible for infinite pan */}
                <g>
                  {/* Pre-render 21 copies (-10 to +10) for infinite horizontal panning */}
                  {copyIndices.map((copyIndex) => (
                    <g
                      key={copyIndex}
                      transform={`translate(${copyIndex * worldWidth}, 0)`}
                    >
                    {/* All countries background - use reference */}
                    {allCountriesPath && <use href="#world-bg" style={{ pointerEvents: 'none' }} />}

                    {/* Target country highlight - CSS animation instead of Framer Motion */}
                    {targetCountryPath && (
                      copyIndex === 0 && !shouldReduceMotion ? (
                        // Animated version only on main copy (index 0)
                        <path
                          d={targetCountryPath}
                          fill="white"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          vectorEffect="non-scaling-stroke"
                          className="text-primary animate-draw-path"
                          pathLength="1"
                          style={{ pointerEvents: 'none' }}
                        />
                      ) : (
                        // Static version on copies (uses reference)
                        <use href="#target-country" style={{ pointerEvents: 'none' }} />
                      )
                    )}

                    {/* Marker for small countries - helps locate tiny islands */}
                    {showTinyMarker && markerPosition && copyIndex === 0 && (
                      <g transform={`translate(${markerPosition.x}, ${markerPosition.y})`}>
                        {/* Scale inversely to currentZoom for consistent screen size */}
                        {/* currentZoom already includes initialZoom (it's d3's k value) */}
                        <g transform={`scale(${1 / currentZoom})`}>
                          {/* Pulsing ring */}
                          {!shouldReduceMotion && (
                            <circle
                              cx={0}
                              cy={0}
                              r={20}
                              fill="none"
                              stroke="currentColor"
                              strokeWidth={2}
                              className="text-primary animate-ping-slow"
                            />
                          )}
                          {/* Main dot */}
                          <circle
                            cx={0}
                            cy={0}
                            r={10}
                            fill="currentColor"
                            className="text-primary"
                          />
                          <circle cx={0} cy={0} r={4} fill="white" />
                        </g>
                      </g>
                    )}
                    </g>
                  ))}
                </g>
          </g>
        </svg>
      )}

      {/* Loading overlay */}
      {isLoading && (
        <div className="absolute inset-0 bg-background/50 flex items-center justify-center">
          <div className="animate-spin w-6 h-6 border-2 border-primary border-t-transparent rounded-full" />
        </div>
      )}
    </div>
  );
});

// Re-export as default for drop-in replacement
export { CountryLocatorOptimized as CountryLocator };

// Export preload function for eager initialization
export function preloadCountryLocator(): void {
  // Worker is already preloading on module import
  // This function can be called to ensure the module is loaded early
  getGeoWorker();
}
